import { useMemo, useState } from 'react'
import type { AdminCampaignListItem, OperatingCostCategory, OperatingCostCreditInstallment } from '@/types/api'
import {
  displayOperatingCostCategoryName,
  formatCategoryTotal,
} from '../lib/operating-costs'
import {
  creditDefaultFormKey,
  draftsFromCreditInstallments,
  emptyCreditInstallmentDrafts,
  parseCreditInstallmentDrafts,
  type CreditInstallmentDraft,
} from '../lib/creditInstallmentDrafts'
import { useCreditFees } from '../hooks/useCreditFees'
import styles from '../CustosOperacionaisPage.module.css'
import { CreditInstallmentEditor } from './CreditInstallmentEditor'
import { OperatingCostCampaignPicker } from './OperatingCostCampaignPicker'
import { PaymentFeePostingsSection } from './PaymentFeePostingsSection'

type CreditFeePanelProps = {
  category: OperatingCostCategory
  onSaved?: () => Promise<void> | void
}

export function CreditFeePanel({ category, onSaved }: CreditFeePanelProps) {
  const fees = useCreditFees({ onSaved })
  const [selectedCampaign, setSelectedCampaign] = useState<AdminCampaignListItem | null>(null)
  const [editingCampaignId, setEditingCampaignId] = useState<number | null>(null)
  const [campaignDrafts, setCampaignDrafts] = useState<CreditInstallmentDraft[]>(emptyCreditInstallmentDrafts)
  const [campaignFormError, setCampaignFormError] = useState<string | null>(null)
  const [pendingCampaignDelete, setPendingCampaignDelete] = useState<number | null>(null)

  const defaultFees = fees.data?.default ?? null
  const campaigns = fees.data?.campaigns
  const excludeIds = useMemo(
    () => (campaigns ?? []).map((item) => item.campaign_id),
    [campaigns]
  )

  function submitCampaign(campaignId: number) {
    const parsed = parseCreditInstallmentDrafts(campaignDrafts)
    if (typeof parsed === 'string') {
      setCampaignFormError(parsed)
      return
    }
    setCampaignFormError(null)
    void fees.saveCampaign(campaignId, parsed.installments).then((ok) => {
      if (ok) {
        setSelectedCampaign(null)
        setEditingCampaignId(null)
      }
    })
  }

  return (
    <article className={styles.categoryCard} aria-labelledby={`operating-cost-category-${category.id}`}>
      <header className={styles.categoryHeader}>
        <div className={styles.categoryHeading}>
          <h3 id={`operating-cost-category-${category.id}`} className={styles.categoryTitle}>
            {displayOperatingCostCategoryName(category)}
          </h3>
          <span className={styles.badge}>Padrão</span>
        </div>
        <p className={styles.categoryTotal}>{formatCategoryTotal(category)}</p>
      </header>

      {fees.error ? (
        <p className={styles.inlineError} role="alert">
          {fees.error}
        </p>
      ) : null}
      {fees.success ? (
        <p className={styles.bannerSuccess} role="status">
          {fees.success}
        </p>
      ) : null}

      <section className={`${styles.feeSection} ${styles.feeSectionHighlighted}`} aria-labelledby={`credit-default-${category.id}`}>
        <div className={styles.sectionHeading}>
          <h4 id={`credit-default-${category.id}`} className={styles.sectionTitle}>
            Valores padrão
          </h4>
          <span className={styles.badge}>Padrão — todas as campanhas</span>
        </div>
        <p className={styles.othersCopy}>
          Configure 1x a 12x. A taxa é obrigatória em cada parcela. Acréscimo percentual e em reais
          são opcionais. O total da parcela é taxa + acréscimo percentual; o valor em reais fica à
          parte. Vale para todas as campanhas, exceto as personalizadas abaixo.
        </p>
        {fees.isLoading && fees.data === null ? (
          <p className={styles.empty}>Carregando taxas padrão...</p>
        ) : (
          <CreditDefaultForm
            key={creditDefaultFormKey(defaultFees)}
            categoryId={category.id}
            defaultFees={defaultFees}
            isSaving={fees.isSaving}
            onSave={(installments) => {
              void fees.saveDefault(installments)
            }}
            onDelete={() => fees.deleteDefault()}
          />
        )}
      </section>

      <section className={styles.feeSection} aria-labelledby={`credit-custom-${category.id}`}>
        <h4 id={`credit-custom-${category.id}`} className={styles.sectionTitle}>
          Valores personalizados por campanha
        </h4>
        <p className={styles.othersCopy}>
          A campanha personalizada precisa das 12 parcelas e substitui o padrão por completo.
        </p>

        <OperatingCostCampaignPicker
          excludeIds={excludeIds}
          disabled={fees.isSaving}
          onSelect={(campaign) => {
            setSelectedCampaign(campaign)
            setEditingCampaignId(null)
            setCampaignDrafts(
              defaultFees
                ? draftsFromCreditInstallments(defaultFees.installments)
                : emptyCreditInstallmentDrafts()
            )
            setCampaignFormError(null)
          }}
        />

        {selectedCampaign ? (
          <div className={styles.feeForm}>
            <p className={styles.othersCopy}>
              Novas taxas para {selectedCampaign.name}
              {selectedCampaign.institution_name ? ` · ${selectedCampaign.institution_name}` : ''}.
            </p>
            <CreditInstallmentEditor
              drafts={campaignDrafts}
              disabled={fees.isSaving}
              idPrefix={`credit-new-${category.id}`}
              onChange={setCampaignDrafts}
            />
            <div className={styles.filterActions}>
              <button
                type="button"
                className={styles.primaryButton}
                disabled={fees.isSaving}
                onClick={() => submitCampaign(selectedCampaign.id)}
              >
                {fees.isSaving ? 'Salvando...' : 'Salvar campanha'}
              </button>
              <button
                type="button"
                className={styles.ghostButton}
                onClick={() => setSelectedCampaign(null)}
              >
                Cancelar
              </button>
            </div>
          </div>
        ) : null}

        {!campaigns || campaigns.length === 0 ? (
          <p className={styles.empty}>Nenhuma campanha personalizada ainda.</p>
        ) : (
          <ul className={styles.campaignFeeList}>
            {campaigns.map((item) => {
              const isEditing = editingCampaignId === item.campaign_id
              return (
                <li key={item.campaign_id} className={styles.campaignFeeItem}>
                  <div className={styles.campaignFeeHeading}>
                    <strong>{item.campaign_name}</strong>
                    <span className={styles.campaignResultMeta}>{item.institution_name}</span>
                    <span>12 parcelas</span>
                  </div>
                  {isEditing ? (
                    <div className={styles.feeForm}>
                      <CreditInstallmentEditor
                        drafts={campaignDrafts}
                        disabled={fees.isSaving}
                        idPrefix={`credit-edit-${item.campaign_id}`}
                        onChange={setCampaignDrafts}
                      />
                      <div className={styles.filterActions}>
                        <button
                          type="button"
                          className={styles.primaryButton}
                          disabled={fees.isSaving}
                          onClick={() => submitCampaign(item.campaign_id)}
                        >
                          {fees.isSaving ? 'Salvando...' : 'Salvar campanha'}
                        </button>
                        <button
                          type="button"
                          className={styles.ghostButton}
                          onClick={() => setEditingCampaignId(null)}
                        >
                          Cancelar
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className={styles.filterActions}>
                      <button
                        type="button"
                        className={styles.secondaryButton}
                        disabled={fees.isSaving}
                        onClick={() => {
                          setSelectedCampaign(null)
                          setEditingCampaignId(item.campaign_id)
                          setCampaignDrafts(draftsFromCreditInstallments(item.installments))
                          setCampaignFormError(null)
                        }}
                      >
                        Editar
                      </button>
                      {pendingCampaignDelete === item.campaign_id ? (
                        <>
                          <button
                            type="button"
                            className={styles.dangerButton}
                            disabled={fees.isSaving}
                            onClick={() => {
                              void fees.deleteCampaign(item.campaign_id).then((ok) => {
                                if (ok) setPendingCampaignDelete(null)
                              })
                            }}
                          >
                            Confirmar exclusão
                          </button>
                          <button
                            type="button"
                            className={styles.ghostButton}
                            onClick={() => setPendingCampaignDelete(null)}
                          >
                            Cancelar
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          className={styles.dangerButton}
                          disabled={fees.isSaving}
                          onClick={() => setPendingCampaignDelete(item.campaign_id)}
                        >
                          Excluir
                        </button>
                      )}
                    </div>
                  )}
                </li>
              )
            })}
          </ul>
        )}
        {campaignFormError ? (
          <p className={styles.inlineError} role="alert">
            {campaignFormError}
          </p>
        ) : null}
      </section>

      <PaymentFeePostingsSection
        categoryId={category.id}
        emptyMessage="Nenhum lançamento de taxa de crédito neste filtro."
      />
    </article>
  )
}

type CreditDefaultFormProps = {
  categoryId: number
  defaultFees: { installments: OperatingCostCreditInstallment[] } | null
  isSaving: boolean
  onSave: (
    installments: { installment: number; rate: number; surchargeRate: number; surchargeAmount: number }[]
  ) => void
  onDelete: () => Promise<boolean>
}

function CreditDefaultForm({
  categoryId,
  defaultFees,
  isSaving,
  onSave,
  onDelete,
}: CreditDefaultFormProps) {
  const [drafts, setDrafts] = useState<CreditInstallmentDraft[]>(
    defaultFees ? draftsFromCreditInstallments(defaultFees.installments) : emptyCreditInstallmentDrafts()
  )
  const [formError, setFormError] = useState<string | null>(null)
  const [pendingDefaultDelete, setPendingDefaultDelete] = useState(false)

  function submitDefault() {
    const parsed = parseCreditInstallmentDrafts(drafts)
    if (typeof parsed === 'string') {
      setFormError(parsed)
      return
    }
    setFormError(null)
    onSave(parsed.installments)
  }

  return (
    <>
      <CreditInstallmentEditor
        drafts={drafts}
        disabled={isSaving}
        idPrefix={`credit-default-${categoryId}`}
        onChange={setDrafts}
      />
      <div className={styles.filterActions}>
        <button type="button" className={styles.primaryButton} disabled={isSaving} onClick={submitDefault}>
          {isSaving ? 'Salvando...' : 'Salvar padrão'}
        </button>
        {defaultFees ? (
          pendingDefaultDelete ? (
            <>
              <button
                type="button"
                className={styles.dangerButton}
                disabled={isSaving}
                onClick={() => {
                  void onDelete().then((ok) => {
                    if (ok) setPendingDefaultDelete(false)
                  })
                }}
              >
                Confirmar exclusão
              </button>
              <button
                type="button"
                className={styles.ghostButton}
                onClick={() => setPendingDefaultDelete(false)}
              >
                Cancelar
              </button>
            </>
          ) : (
            <button
              type="button"
              className={styles.dangerButton}
              disabled={isSaving}
              onClick={() => setPendingDefaultDelete(true)}
            >
              Excluir padrão
            </button>
          )
        ) : null}
      </div>
      {formError ? (
        <p className={styles.inlineError} role="alert">
          {formError}
        </p>
      ) : null}
    </>
  )
}
